import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Switch } from "design-system/atoms/Switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { ArrowUpRight } from "lucide-react"
import { Pulse } from "modules/shared/components/Miscelanea/Pulse"
import { useCreateTopupsSetupMutation } from "modules/subscription/hooks/useCreateTopupsSetupMutation"
import { useDeleteTopupsSetupMutation } from "modules/subscription/hooks/useDeleteTopupsSetupMutation"
import { useGetProducts } from "modules/subscription/hooks/useGetProducts"
import { useGetSubscription } from "modules/subscription/hooks/useGetSubscription"
import { useUpdateTopupsSetupMutation } from "modules/subscription/hooks/useUpdateTopupsSetupMutation"
import { useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { z } from "zod"

type Props = {
  onClose: () => void
}

const Schema = z.object({
  enabled: z.boolean(),
  autoTopUpPriceId: z.string().optional(),
  monthlyLimitEnabled: z.boolean(),
  monthlyLimit: z.number().optional().nullable(),
})

export function ConfigureTopups({ onClose }: Props) {
  const { t } = useTranslation()
  const { data: subscription } = useGetSubscription()
  const { data: products } = useGetProducts()

  const extraCreditsPrices = useMemo(() => {
    const activeProduct = products?.find((p) => p.isActive)
    const product = activeProduct ?? products?.[0]
    return product?.extraCreditsPrices ?? []
  }, [products])

  const priceForCurrentAutoTopUp = extraCreditsPrices.find(
    (p) => p.id === subscription?.autoTopUpPriceId,
  )

  const { mutateAsync: createSetup, isPending: isCreatingSetup } = useCreateTopupsSetupMutation()
  const { mutateAsync: updateSetup, isPending: isUpdatingSetup } = useUpdateTopupsSetupMutation()
  const { mutateAsync: deleteSetup, isPending: isDeletingSetup } = useDeleteTopupsSetupMutation()

  const isPending = isCreatingSetup || isUpdatingSetup || isDeletingSetup

  const form = useForm<z.infer<typeof Schema>>({
    resolver: zodResolver(Schema),
    defaultValues: {
      enabled: Boolean(subscription?.autoTopUpPriceId),
      autoTopUpPriceId: priceForCurrentAutoTopUp?.id ?? extraCreditsPrices[0]?.id,
      monthlyLimitEnabled: subscription?.autoTopUpMonthlyLimit != null,
      monthlyLimit: subscription?.autoTopUpMonthlyLimit,
    },
  })

  useEffect(() => {
    form.setValue("enabled", Boolean(subscription?.autoTopUpPriceId), { shouldDirty: false })
    form.setValue("monthlyLimitEnabled", subscription?.autoTopUpMonthlyLimit != null, {
      shouldDirty: false,
    })
    form.setValue("monthlyLimit", subscription?.autoTopUpMonthlyLimit, { shouldDirty: false })
    if (subscription?.autoTopUpPriceId) {
      form.setValue("autoTopUpPriceId", subscription.autoTopUpPriceId, { shouldDirty: false })
    } else if (extraCreditsPrices[0]?.id) {
      form.setValue("autoTopUpPriceId", extraCreditsPrices[0].id, { shouldDirty: false })
    }
  }, [subscription, extraCreditsPrices, form])

  async function onSubmit(data: z.infer<typeof Schema>) {
    let kind: "delete" | "create" | "update"

    if (subscription?.autoTopUpPriceId && !data.enabled) {
      kind = "delete"
    } else if (!subscription?.autoTopUpPriceId && data.enabled) {
      kind = "create"
    } else {
      kind = "update"
    }

    const autoTopUpPriceId =
      data.autoTopUpPriceId ?? subscription?.autoTopUpPriceId ?? extraCreditsPrices[0]?.id
    const monthlyLimit = data.monthlyLimitEnabled ? data.monthlyLimit : undefined

    switch (kind) {
      case "create":
        if (!autoTopUpPriceId) {
          toast.error(t("subscription.usage.autoTopup.error"))
          return
        }
        const { url } = await createSetup({
          monthlyLimit,
          autoTopUpPriceId,
        })
        if (url) {
          window.location.href = url
        } else {
          toast.error(t("subscription.usage.autoTopup.error"))
        }
        break

      case "update":
        if (!autoTopUpPriceId) {
          toast.error(t("subscription.usage.autoTopup.error"))
          return
        }
        await updateSetup({
          monthlyLimit,
          autoTopUpPriceId,
        })
        toast.success(t("subscription.usage.autoTopup.updated"))
        onClose()
        break

      case "delete":
        await deleteSetup()
        toast.success(t("subscription.usage.autoTopup.deleted"))
        onClose()
        break
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>
            <div className="flex flex-row items-center gap-2">
              <span>
                {subscription?.autoTopUpPriceId
                  ? t("subscription.usage.autoTopup.active")
                  : t("subscription.usage.autoTopup.title")}
              </span>
              {subscription?.autoTopUpPriceId && <Pulse />}
            </div>
          </DialogTitle>
          <DialogDescription>{t("subscription.usage.autoTopup.description")}</DialogDescription>
          <Form
            {...form}
            errorTranslations={getErrorTranslations()}
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-6 py-4"
          >
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem>
                  <div className="flex flex-row items-center gap-4">
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isPending}
                      id={field.name}
                      className="data-[state=checked]:bg-chart-1"
                    />
                    <FormLabel className="flex flex-col gap-1 mt-0.5" htmlFor={field.name}>
                      <span>{t("subscription.usage.autoTopup.enable.label")}</span>
                      <FormDescription>
                        {t("subscription.usage.autoTopup.enable.description")}
                      </FormDescription>
                    </FormLabel>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {extraCreditsPrices.length > 0 && (
              <FormField
                control={form.control}
                name="autoTopUpPriceId"
                render={({ field }) => {
                  const singleOption = extraCreditsPrices[0]
                  let displayValue = ""
                  if (singleOption?.unit_amount != null) {
                    displayValue = t("subscription.usage.autoTopup.creditsAmount.option", {
                      credits: singleOption.credits,
                      amount: (singleOption.unit_amount / 100).toFixed(2),
                    })
                  } else if (singleOption != null) {
                    displayValue = `${singleOption.credits} credits`
                  }

                  return (
                    <FormItem>
                      <FormLabel>{t("subscription.usage.autoTopup.creditsAmount.label")}</FormLabel>
                      {extraCreditsPrices.length === 1 ? (
                        <div className="flex h-9 w-full items-center rounded-md border border-input bg-muted px-3 py-2 text-sm">
                          {displayValue}
                        </div>
                      ) : (
                        <Select
                          value={field.value ?? ""}
                          onValueChange={field.onChange}
                          disabled={isPending}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue
                                placeholder={t("subscription.usage.autoTopup.creditsAmount.label")}
                              />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {extraCreditsPrices.map((option) => (
                              <SelectItem key={option.id} value={option.id}>
                                {option.unit_amount != null
                                  ? t("subscription.usage.autoTopup.creditsAmount.option", {
                                      credits: option.credits,
                                      amount: (option.unit_amount / 100).toFixed(2),
                                    })
                                  : `${option.credits} credits`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {extraCreditsPrices.length > 1 && (
                        <FormDescription>
                          {t("subscription.usage.autoTopup.creditsAmount.description")}
                        </FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
            )}
            <FormField
              control={form.control}
              name="monthlyLimitEnabled"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <div className="flex flex-row items-center justify-between gap-2">
                    <FormLabel htmlFor={field.name}>
                      {t("subscription.usage.autoTopup.monthlyLimit.label")}
                    </FormLabel>
                    <FormControl>
                      <Switch
                        id={field.name}
                        checked={field.value}
                        onCheckedChange={(checked: boolean) => {
                          field.onChange(checked)
                          if (!checked) {
                            form.setValue("monthlyLimit", undefined, { shouldDirty: true })
                          }
                        }}
                        disabled={isPending}
                      />
                    </FormControl>
                  </div>
                  {field.value && (
                    <FormField
                      control={form.control}
                      name="monthlyLimit"
                      render={({ field: limitField }) => (
                        <>
                          <FormControl>
                            <Input
                              {...limitField}
                              onChange={(e) => {
                                const value = e.target.value
                                if (value === "") {
                                  limitField.onChange(null)
                                } else if (/^\d+$/.test(value)) {
                                  limitField.onChange(Number(value))
                                }
                              }}
                              value={limitField.value ?? ""}
                              placeholder={t(
                                "subscription.usage.autoTopup.monthlyLimit.placeholder",
                              )}
                              disabled={isPending}
                            />
                          </FormControl>
                          <FormDescription>
                            {t("subscription.usage.autoTopup.monthlyLimit.description")}
                          </FormDescription>
                          <FormMessage />
                        </>
                      )}
                    />
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          </Form>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t("shared.cancel")}
          </Button>
          {subscription?.autoTopUpPriceId ? (
            <Button
              type="submit"
              variant={
                subscription?.autoTopUpPriceId && !form.watch("enabled") ? "destructive" : "default"
              }
              disabled={
                isPending ||
                !form.formState.isDirty ||
                (form.watch("enabled") &&
                  extraCreditsPrices.length > 0 &&
                  !form.watch("autoTopUpPriceId"))
              }
              className="flex flex-row items-center gap-2"
              onClick={() => {
                form.handleSubmit(onSubmit)()
              }}
            >
              <span>
                {subscription?.autoTopUpPriceId && !form.watch("enabled")
                  ? t("subscription.usage.autoTopup.cta.disable")
                  : t("subscription.usage.autoTopup.cta.rest")}
              </span>
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={
                !form.watch("enabled") ||
                isPending ||
                (form.watch("enabled") &&
                  extraCreditsPrices.length > 0 &&
                  !form.watch("autoTopUpPriceId"))
              }
              className="flex flex-row items-center gap-2"
              onClick={() => {
                form.handleSubmit(onSubmit)()
              }}
            >
              <span>{t("subscription.usage.autoTopup.cta.firstTime")}</span>
              <ArrowUpRight className="w-4 h-4" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
